import { FORM_ERROR } from 'final-form'
import { CategoriaSubstanciaAlergia } from 'graphql/types.generated'
import moment from 'moment'
import { isUndefinedOrNull } from 'util/checks'
import { createValidator, ErrorObject, range, required } from 'util/validation'
import { isEmpty, isValid } from 'util/validation/Util'

import { Problema } from '../../../aside/types/ProblemaModel'
import { DataIdadeFieldModel } from '../../../components/DataIdadeField'
import { TipoPreNatal } from '../../../pre-natal/model-preNatal'
import { isCidFromFamiliaZ34 } from '../../../pre-natal/util-preNatal'
import { validateProblemaCondicaoPreNatal } from '../../../pre-natal/validator-preNatal'
import { AlergiaReacaoModel } from '../alergias-reacoes/model'
import { ProblemaCondicaoModel } from './model-problemasCondicoes'
import {
  hasCondicaoAtivaGravidezDuplicada,
  hasW78ExistenteDuplicado,
  isProblemaCondicaoComCiapW78,
  isProblemaCondicaoComCiapW78Resolvido,
} from './utils/verifications-preNatal'
import {
  hasProblemaDuplicado,
  hasProblemaDuplicadoEmAtivosLatentes,
  hasProblemaDuplicadoLPC,
} from './utils/verifications-problemasCondicoes'

const MESSAGE_RELACAO_DUPLICADA_A_EVOLUIR =
  'Este problema/condição já foi adicionado e deve ser evoluído na Avaliação do SOAP.'
const MESSAGE_RELACAO_DUPLICADA_COM_ATIVO_OU_LATENTE =
  'Não é possível adicionar problemas e/ou condições ativas ou latentes repetidas para um mesmo par de CID10 e CIAP.'

export const MESSAGE_CONDICAO_ATIVA_GRAVIDEZ_DUPLICADA = 'Já existe uma condição de gravidez ativa.'
export const MESSAGE_RELACAO_DUPLICADA = 'Este relacionamento de CIAP2 e CID10 já está adicionado.'

export const alergiasReacoesValidator = (dataNascimentoCidadao: LocalDate, dataInicioAtendimento: Instant) =>
  createValidator<AlergiaReacaoModel>(
    {
      dataInicio: dataProblemaValidator(dataNascimentoCidadao, dataInicioAtendimento),
      categoriaSubstancia: [required],
    },
    (formValues, errors) => {
      if (formValues?.categoriaSubstancia && formValues.categoriaSubstancia === CategoriaSubstanciaAlergia.BIOLOGICO) {
        errors.substanciaImunobiologico = required(formValues?.substanciaImunobiologico)
      } else if (
        formValues?.categoriaSubstancia &&
        formValues.categoriaSubstancia === CategoriaSubstanciaAlergia.MEDICAMENTO
      ) {
        errors.substanciaMedicamento = required(formValues?.substanciaMedicamento)
      } else {
        errors.substanciaCbara = required(formValues?.substanciaCbara)
      }

      return errors
    }
  )

export const problemasCondicoesValidator = (
  somenteCiap: boolean,
  values: ProblemaCondicaoModel[],
  isEdit: boolean,
  dataInicioAtendimento: Instant,
  dataNascimentoCidadao: LocalDate,
  problemasAtivosLatentes: Problema[],
  initialValues: ProblemaCondicaoModel,
  isMasculinoAndNotHaveIdentidadeGenero: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  tipoPreNatal?: TipoPreNatal,
  isLPC?: boolean
) =>
  createValidator<ProblemaCondicaoModel>(
    {
      dataInicio: dataProblemaValidator(dataNascimentoCidadao, dataInicioAtendimento),
      dataFim: dataProblemaValidator(dataNascimentoCidadao, dataInicioAtendimento),
    },
    (formValues, errors) => {
      const emptyCiapCid = isEmpty(formValues?.ciap) && isEmpty(formValues?.cid)

      if (somenteCiap) {
        errors.ciap = required(formValues?.ciap)
      } else {
        errors[FORM_ERROR] = emptyCiapCid ? 'Pelo menos um dos campos é de preenchimento obrigatório.' : null
        validateRelacaoCiapCidPreNatal(formValues, errors)
      }

      if (!emptyCiapCid && isValid(errors)) {
        validateRelacaoDuplicada(values, formValues, isEdit, errors, isLPC, problemasAtivosLatentes, somenteCiap)
      }

      errors = validateProblemaCondicaoPreNatal(
        formValues,
        errors,
        isEdit,
        isMasculinoAndNotHaveIdentidadeGenero,
        hasProblemaComCiapW78AtivoPersistido,
        initialValues,
        isLPC,
        tipoPreNatal
      )

      if (formValues?.incluirNaListaProblemas || isLPC) {
        if (isUndefinedOrNull(formValues?.situacaoProblema))
          errors.situacaoProblema = required(formValues?.situacaoProblema)
        if (
          formValues?.dataFim?.data &&
          formValues?.dataInicio?.data &&
          moment(formValues.dataFim.data).isBefore(moment(formValues.dataInicio.data))
        ) {
          errors.dataFim = { data: 'Deve ser posterior ou igual ao Início do problema.' }
        }
      }

      return errors
    }
  )

const dataProblemaValidator = (dataMinima: LocalDate, dataMaxima: number) =>
  createValidator<DataIdadeFieldModel>({}, (formValues, errors) => {
    if (formValues) {
      errors.idade = range(0, 11)(formValues?.idade?.meses) && 'Meses deve ter valor entre 0 e 11.'

      if (!errors.idade) errors.idade = range(0, 999)(formValues?.idade?.anos) && 'Anos deve ter valor entre 0 e 999.'

      if (moment(formValues.data).isBefore(moment(dataMinima))) {
        errors.data = 'Deve ser posterior ou igual à Data de nascimento do cidadão.'
      }

      if (moment(formValues.data).startOf('day').isAfter(moment(dataMaxima))) {
        errors.data = 'Deve ser anterior ou igual à Data do atendimento.'
      }
    }

    return errors
  })

function validateRelacaoDuplicada(
  values: ProblemaCondicaoModel[],
  formValues: ProblemaCondicaoModel,
  isEdit: boolean,
  errors: ErrorObject<ProblemaCondicaoModel>,
  isLPC: boolean,
  problemasAtivosLatentes: Problema[],
  somenteCiap: boolean
) {
  isLPC
    ? validateRelacaoDuplicadaLPC(formValues, values, isEdit, somenteCiap, problemasAtivosLatentes, errors)
    : validateRelacaoDuplicadaAvaliacao(formValues, values, isEdit, somenteCiap, problemasAtivosLatentes, errors)
}

function validateRelacaoDuplicadaLPC(
  formValues: ProblemaCondicaoModel,
  values: ProblemaCondicaoModel[],
  isEdit: boolean,
  somenteCiap: boolean,
  problemasAtivosLatentes: Problema[],
  errors: ErrorObject<ProblemaCondicaoModel>
) {
  const hasItemDuplicado = hasProblemaDuplicadoLPC(formValues, values, isEdit, somenteCiap)
  const hasItemDuplicadoAtivoOuLatente = hasProblemaDuplicadoEmAtivosLatentes(formValues, problemasAtivosLatentes, true)
  const hasCondicaoGravidezDuplicada = hasCondicaoAtivaGravidezDuplicada(
    isEdit,
    formValues,
    values,
    problemasAtivosLatentes
  )

  fillErrorsWithRelacaoDuplicadaMessage(
    hasCondicaoGravidezDuplicada,
    errors,
    hasItemDuplicado,
    hasItemDuplicadoAtivoOuLatente,
    MESSAGE_RELACAO_DUPLICADA_A_EVOLUIR
  )
}

export function validateRelacaoDuplicadaAvaliacao(
  problemaAvaliado: ProblemaCondicaoModel,
  values: ProblemaCondicaoModel[],
  isEdit: boolean,
  somenteCiap: boolean,
  problemasAtivosLatentes: Problema[],
  errors: ErrorObject<ProblemaCondicaoModel>
) {
  const problemaAEvouirId = problemaAvaliado.problemaId
  const problemasAtivosLatentesSemPossivelEvolucao = problemasAtivosLatentes.filter(
    (item) => item.id !== problemaAEvouirId
  )

  const hasItemDuplicado = hasProblemaDuplicado(problemaAvaliado, values, isEdit, somenteCiap)
  const hasItemDuplicadoAtivoOuLatente =
    problemaAvaliado.incluirNaListaProblemas &&
    hasProblemaDuplicadoEmAtivosLatentes(problemaAvaliado, problemasAtivosLatentesSemPossivelEvolucao, false)
  const hasGravidezDuplicada = hasCondicaoAtivaGravidezDuplicada(
    isEdit,
    problemaAvaliado,
    values,
    problemasAtivosLatentesSemPossivelEvolucao
  )
  const existeW78DuplicadoNaLPC = hasW78ExistenteDuplicado(problemasAtivosLatentes)
  const shouldSkipValidation = isProblemaCondicaoComCiapW78Resolvido(problemaAvaliado) && existeW78DuplicadoNaLPC

  if (!shouldSkipValidation) {
    fillErrorsWithRelacaoDuplicadaMessage(
      hasGravidezDuplicada,
      errors,
      hasItemDuplicado,
      hasItemDuplicadoAtivoOuLatente,
      MESSAGE_RELACAO_DUPLICADA_COM_ATIVO_OU_LATENTE
    )
  }
}

function validateRelacaoCiapCidPreNatal(formValues: ProblemaCondicaoModel, errors: ErrorObject<ProblemaCondicaoModel>) {
  const isCiapW78 = isProblemaCondicaoComCiapW78(formValues)
  const isCidFamiliaZ34 = isCidFromFamiliaZ34(formValues?.cid?.codigo)
  if ((isCiapW78 && !isCidFamiliaZ34) || (!isCiapW78 && isCidFamiliaZ34)) {
    errors[FORM_ERROR] = 'Pré-natal precisa de CIAP2 e CID10 relacionados.'
  }
}

export function fillErrorsWithRelacaoDuplicadaMessage(
  isCondicaoAtivaGravidezDuplicada: boolean,
  errors: ErrorObject<ProblemaCondicaoModel>,
  hasItemDuplicado: boolean,
  hasItemDuplicadoAtivoOuLatente: boolean,
  messageRelacaoDuplicadaAtivoOuLatente: string
) {
  if (isCondicaoAtivaGravidezDuplicada) {
    errors[FORM_ERROR] = MESSAGE_CONDICAO_ATIVA_GRAVIDEZ_DUPLICADA
  } else {
    errors[FORM_ERROR] = hasItemDuplicado
      ? MESSAGE_RELACAO_DUPLICADA
      : hasItemDuplicadoAtivoOuLatente && messageRelacaoDuplicadaAtivoOuLatente
  }
}
